import React, { useMemo } from 'react';
import { VendorGetServerSideProps } from 'vendor/utils/VendorGetServerSideProps';
import { videoQueryByIdOrSlugFactory } from 'src/modules/videos/utils/videoQueryFactory';
import { route } from 'src/services/route';
import {
  VideoPageTemplate,
  VideoPageLoader,
} from 'src/modules/videos/components/VideoPage';
import { VideoPageSEO } from 'src/modules/videos/components/VideoPage/VideoPageSEO';
import { NeauviaPlayerProps } from 'src/components/player';
import { fetchSitemapVideos } from 'src/modules/videos/hooks/useVideosSitemapQuery';
import { configureSSR } from 'src/utils/configureSSR';
import { tabsFactory } from 'src/modules/materials/utils/tabsFactory';
import { MaterialPageTabId } from 'src/modules/materials/components/MaterialPage/MaterialPageTabs/types';
import { useVideosDetailsQuery } from 'src/modules/videos/hooks/useVideosDetailsQuery';
import { useUserNotesQuery } from 'src/modules/user/hooks/useUserNotesQuery';
import { useUserPermissions } from 'src/modules/user/hooks/useUserPermissions';
import { isVideoTaxonomyInvalid } from 'src/modules/videos/utils/isVideoTaxonomyInvalid';

const playerProps: Partial<NeauviaPlayerProps> = {
  controls: {
    title: {
      enabled: true,
      backHref: true,
    },
  },
};

const VideoPage = () => {
  const { data: video, isLoading } = useVideosDetailsQuery();
  const { data: notes } = useUserNotesQuery(video);

  const [userPermissions] = useUserPermissions();

  const videoTabs = useMemo(
    () =>
      tabsFactory(video)([
        MaterialPageTabId.Details,
        MaterialPageTabId.Products,
        MaterialPageTabId.Notes,
        ...(userPermissions.kol
          ? [{ id: MaterialPageTabId.UserNotes, count: notes?.length }]
          : []),
      ]),
    [video, userPermissions.kol, notes?.length],
  );

  if (video && !isLoading) {
    return (
      <VideoPageTemplate
        tabs={videoTabs}
        video={video}
        playerProps={playerProps}
      >
        <VideoPageSEO video={video} />
      </VideoPageTemplate>
    );
  }

  return <VideoPageLoader />;
};

export const getServerSideProps = VendorGetServerSideProps(async (ctx) => {
  const { subjectOrSlug, bodyPartOrSlug, slug } = ctx.params ?? {};

  const videoIdOrSlug = String(slug || bodyPartOrSlug || subjectOrSlug);
  const query = videoQueryByIdOrSlugFactory(ctx.apis, videoIdOrSlug);

  const video = await ctx.queryClient.fetchQuery({
    ...query,
    cacheTime: 0,
  });

  if (!video || isVideoTaxonomyInvalid(video)) {
    return {
      notFound: true,
    };
  }

  return configureSSR(ctx, {
    layout: {
      variant: 'default',
      tvbar: true,
    },
    cacheControl: {
      maxAge: 900,
      sMaxAge: 3600,
    },
    actions: {
      common: () => [ctx.queryClient.setQueryData(query.queryKey, video)],
    },
  });
});

export async function getSitemap(): Promise<SitemapEntry[]> {
  const videos = await fetchSitemapVideos();

  return videos.map((video) => ({
    loc: route.video(video).page(true),
    lastmod: video.date_updated ?? video.date_created,
    priority: 0.6,
  }));
}

export default VideoPage;
